import { format, isValid } from 'date-fns'
import { ptBR } from 'date-fns/locale/pt-BR'

import { useCakeMakerState, useCakeMakerTotal } from '@/store/useCakeMaker'
import { SquircleAdapter } from '@/components/SquircleAdapter'
import { THEME_PRICE } from '@/config/constants'
import { formatMoney } from '@/utils'

import './styles.scss'

interface ResumeProps {
  cornerRadius?: number
}

export function Resume({ cornerRadius = 16 }: ResumeProps) {
  const {
    orderDate,
    shape,
    weight,
    theme,
    batter,
    fillings,
    topping,
    additional,
    candies,
    delivery,
  } = useCakeMakerState()

  const total = useCakeMakerTotal()

  const isEmpty = !isValid(orderDate)
  const weightValue = weight?.value || '[PESO]'
  const factor = weight?.multiplication_factor || 0

  const renderPrice = (unit: string, price: number, subtotal: number) => (
    <>
      <span className="resume__section__item__calc">
        {unit} × {formatMoney(price)}
      </span>
      <span className="stronger">{formatMoney(subtotal)}</span>
    </>
  )

  return (
    <SquircleAdapter cornerRadius={cornerRadius} className="resume">
      {isEmpty ? (
        <div className="resume__empty">
          <h4 className="resume__empty__title">Nada por aqui!</h4>
          <p className="resume__empty__description">
            Comece a montar seu bolo escolhendo a data de entrega.
          </p>
        </div>
      ) : (
        <div className="resume__items">
          <div className="resume__section">
            {orderDate && (
              <div className="resume__section__item">
                <p className="resume__section__item__label">Data de entrega</p>
                <p className="resume__section__item__value">
                  {format(orderDate, 'dd MMM yyyy', { locale: ptBR })}
                </p>
              </div>
            )}

            {weight && (
              <div className="resume__section__item">
                <p className="resume__section__item__label">Quantidade</p>
                <p className="resume__section__item__value">
                  {weight.value} • {weight.description}
                </p>
              </div>
            )}

            {shape && (
              <div className="resume__section__item">
                <p className="resume__section__item__label">Formato</p>
                <p className="resume__section__item__value">{shape.name}</p>
              </div>
            )}
          </div>

          <div className="resume__section">
            {theme && (
              <div className="resume__section__item">
                <p className="resume__section__item__label stronger">
                  Tema {`"${theme}"`}
                </p>
                <p className="resume__section__item__value stronger">
                  {formatMoney(THEME_PRICE)}
                </p>
              </div>
            )}

            {batter && (
              <div className="resume__section__item">
                <p className="resume__section__item__label stronger">
                  Massa de {batter?.name}
                </p>
                <p className="resume__section__item__value">
                  {renderPrice(
                    weightValue,
                    batter?.price || 0,
                    (batter?.price || 0) * factor,
                  )}
                </p>
              </div>
            )}

            {fillings.length > 0 &&
              fillings.map((filling) => (
                <div className="resume__section__item" key={filling.id}>
                  <p className="resume__section__item__label stronger">
                    Recheio de {filling.name}
                  </p>
                  <p className="resume__section__item__value">
                    {filling.price > 0 ? (
                      renderPrice(
                        weightValue,
                        filling.price,
                        filling.price * factor,
                      )
                    ) : (
                      <span className="stronger">Grátis</span>
                    )}
                  </p>
                </div>
              ))}

            {topping && (
              <div className="resume__section__item">
                <p className="resume__section__item__label stronger">
                  Cobertura de {topping.name}
                </p>
                <p className="resume__section__item__value">
                  {renderPrice(
                    weightValue,
                    topping.price,
                    topping.price * factor,
                  )}
                </p>
              </div>
            )}

            {additional.length > 0 &&
              additional.map((additional) => (
                <div className="resume__section__item" key={additional.id}>
                  <p className="resume__section__item__label stronger">
                    Adicional de {additional.name}
                  </p>
                  <p className="resume__section__item__value">
                    {renderPrice(
                      weightValue,
                      additional.price,
                      additional.price * factor,
                    )}
                  </p>
                </div>
              ))}

            {candies.length > 0 &&
              candies.map(
                (candy) =>
                  candy.quantity > 0 && (
                    <div className="resume__section__item" key={candy.id}>
                      <p className="resume__section__item__label stronger">
                        Docinho de {candy.name}
                      </p>
                      <p className="resume__section__item__value">
                        {renderPrice(
                          `${candy.quantity}un`,
                          candy.price,
                          candy.price * candy.quantity,
                        )}
                      </p>
                    </div>
                  ),
              )}

            {delivery?.address && (
              <div className="resume__section__item">
                <p className="resume__section__item__label stronger">
                  Entrega no CEP {delivery.zipCode}
                </p>
                <p className="resume__section__item__value stronger">
                  {formatMoney(delivery.price)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="resume__section">
        <div className="resume__section__item">
          <p className="resume__section__item__label">Total</p>
          <p className="resume__section__item__value price">
            {formatMoney(total)}
          </p>
        </div>
      </div>
    </SquircleAdapter>
  )
}
